/**
 * Minimal typed event emitter.
 *
 * Used for *state changes*, never for per-frame data: emitting iterates an array and
 * invokes closures, which is fine at a few hertz (quality tier changed, aircraft spawned,
 * crash detected) and wrong at 120 Hz. Hot paths call methods directly.
 */

export type Listener<T> = (payload: T) => void;

export class Emitter<TEvents extends Record<string, unknown>> {
  private readonly listeners = new Map<keyof TEvents, Array<Listener<never>>>();

  /** Subscribe. Returns an unsubscribe function. */
  on<K extends keyof TEvents>(event: K, listener: Listener<TEvents[K]>): () => void {
    let list = this.listeners.get(event);
    if (list === undefined) {
      list = [];
      this.listeners.set(event, list);
    }
    list.push(listener as Listener<never>);
    return () => this.off(event, listener);
  }

  /** Subscribe for exactly one emission. */
  once<K extends keyof TEvents>(event: K, listener: Listener<TEvents[K]>): () => void {
    const off = this.on(event, (payload) => {
      off();
      listener(payload);
    });
    return off;
  }

  /** Unsubscribe. Safe to call from inside a listener. */
  off<K extends keyof TEvents>(event: K, listener: Listener<TEvents[K]>): void {
    const list = this.listeners.get(event);
    if (list === undefined) return;
    const index = list.indexOf(listener as Listener<never>);
    if (index >= 0) list.splice(index, 1);
  }

  /**
   * Notify subscribers. Iterates a snapshot index-wise so that a listener unsubscribing
   * itself during dispatch cannot skip its neighbour.
   */
  emit<K extends keyof TEvents>(event: K, payload: TEvents[K]): void {
    const list = this.listeners.get(event);
    if (list === undefined) return;
    for (let i = list.length - 1; i >= 0; i--) {
      (list[i] as Listener<TEvents[K]>)(payload);
    }
  }

  /** How many listeners are attached (all events, or one). */
  listenerCount(event?: keyof TEvents): number {
    if (event !== undefined) return this.listeners.get(event)?.length ?? 0;
    let total = 0;
    for (const list of this.listeners.values()) total += list.length;
    return total;
  }

  /** Drop every listener. */
  clear(): void {
    this.listeners.clear();
  }
}
